import { useMemo, useState } from "react"
import { Link, Navigate, useNavigate, useSearchParams } from "react-router"
import ModuleLayout from "../../components/admin/ModuleLayout"
import Alert from "@/components/ui/alert/Alert"
import { LoadState } from "@/components/ui/LoadState"
import { StatusPill, type PillTone } from "@/components/cccd/StatusPill"
import { AttendanceTable, type AttendanceColumn } from "@/components/cccd/AttendanceTable"
import { AttendanceScopeFilter, type ScopeFilterValue } from "@/components/cccd/AttendanceScopeFilter"
import { useAttendanceSummary } from "@/hooks/useAttendanceSummary"
import { cccdAttendance, type AttendanceSummaryRow } from "@/lib/api/cccdAttendanceClient"
import { eventPhase, filterRows, formatRange, readLastScope, scanPath, type EventPhase } from "@/lib/checkinEvents"
import { getErrorMessage } from "@/lib/errors"

const PHASE_PILL: Record<EventPhase, { tone: PillTone; label: string }> = {
  live: { tone: "success", label: "En curso" },
  upcoming: { tone: "brand", label: "Próximo" },
  past: { tone: "gray", label: "Finalizado" },
}

const btnBase =
  "inline-flex min-h-11 w-full items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 sm:w-auto"

function ScanAction({ row }: { row: AttendanceSummaryRow }) {
  if (!row.attendeeTicketsEnabled) {
    return (
      <>
        <span className="text-sm text-gray-500 sm:hidden dark:text-gray-400">Boletos desactivados para este evento</span>
        <span aria-disabled="true" className={`${btnBase} cursor-not-allowed bg-gray-100 text-gray-400 max-sm:hidden dark:bg-gray-800 dark:text-gray-500`}>
          Escanear
        </span>
      </>
    )
  }
  return (
    <Link to={scanPath(row)} onClick={(e) => e.stopPropagation()} className={`${btnBase} bg-brand-500 text-white shadow-theme-xs hover:bg-brand-600`}>
      Escanear
    </Link>
  )
}

function buildColumns(today: string): AttendanceColumn<AttendanceSummaryRow>[] {
  return [
    {
      key: "name",
      label: "Evento",
      primary: true,
      render: (r) => (
        <div className="min-w-0">
          <p className="font-medium text-gray-900 dark:text-white">{r.name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{r.scope === "event" ? "Nacional" : `Regional · ${r.region ?? "sin región"}`}</p>
        </div>
      ),
    },
    { key: "dates", label: "Fechas", render: (r) => <span className="text-xs text-gray-500 sm:whitespace-nowrap dark:text-gray-400">{formatRange(r.eventStartDate, r.eventEndDate)}</span> },
    {
      key: "status",
      label: "Estado",
      render: (r) => {
        const pill = PHASE_PILL[eventPhase(r, today)]
        return (
          <span className="flex flex-wrap items-center gap-1.5">
            <StatusPill tone={pill.tone} label={pill.label} />
            {!r.attendeeTicketsEnabled && (
              <span className="hidden sm:inline-flex">
                <StatusPill tone="warning" label="Boletos desactivados" />
              </span>
            )}
          </span>
        )
      },
    },
    { key: "tickets", label: "Boletos", align: "right", render: (r) => r.totalTickets },
    { key: "today", label: "Ingresos hoy", align: "right", render: (r) => r.checkedInToday },
    { key: "action", label: "Acción", align: "right", action: true, render: (r) => <ScanAction row={r} /> },
  ]
}

function Group({ title, rows, today, onOpen, showTitle = true }: { title: string; rows: AttendanceSummaryRow[]; today: string; onOpen: (r: AttendanceSummaryRow) => void; showTitle?: boolean }) {
  const columns = useMemo(() => buildColumns(today), [today])
  return (
    <section aria-label={title}>
      {showTitle && (
        <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-white/90">
          {title} <span className="font-normal text-gray-500 dark:text-gray-400">({rows.length})</span>
        </h2>
      )}
      <AttendanceTable
        columns={columns}
        rows={rows}
        rowKey={(r) => `${r.scope}:${r.id}`}
        caption={title}
        emptyMessage="Sin eventos."
        onRowClick={(r) => r.attendeeTicketsEnabled && onOpen(r)}
      />
    </section>
  )
}

export default function CheckInEventsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const today = cccdAttendance.todayIso()
  const { data, isLoading, isError, error } = useAttendanceSummary()
  const [scope, setScope] = useState<ScopeFilterValue>("all")
  const [search, setSearch] = useState("")

  const rows = useMemo(() => filterRows(data ?? [], scope, search), [data, scope, search])
  const groups = useMemo(() => {
    const byStart = (a: AttendanceSummaryRow, b: AttendanceSummaryRow) => a.eventStartDate.localeCompare(b.eventStartDate)
    const live = rows.filter((r) => eventPhase(r, today) === "live").sort(byStart)
    const upcoming = rows.filter((r) => eventPhase(r, today) === "upcoming").sort(byStart)
    const past = rows.filter((r) => eventPhase(r, today) === "past").sort((a, b) => byStart(b, a))
    return { live, upcoming, past }
  }, [rows, today])

  const last = useMemo(() => {
    const [kind, id] = readLastScope().split(":")
    return (data ?? []).find((r) => r.scope === kind && r.id === id && r.attendeeTicketsEnabled)
  }, [data])

  // Enlaces anteriores al lanzador (/cccd/checkin?scope=event:ID) siguen llevando al escaneo de ese evento.
  const [legacyKind, legacyId] = (searchParams.get("scope") ?? "").split(":")
  if ((legacyKind === "event" || legacyKind === "regional") && legacyId) {
    return <Navigate replace to={scanPath({ scope: legacyKind, id: legacyId })} />
  }

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, { label: "CCCD", to: "/cccd" }, "Check-in / Asistencia"]}
      title="Check-in / Asistencia"
      compactOnMobile
      subtitle="Elige el evento que vas a controlar y entra directo al escaneo."
      tabs={[
        { label: "Escanear", to: "/cccd/checkin", active: true },
        { label: "Reporte", to: "/cccd/checkin/report" },
      ]}
      metrics={[]}
    >
      <div className="space-y-6">
        {last && (
          <Link
            to={scanPath(last)}
            className={`${btnBase} bg-brand-500 text-white shadow-theme-xs hover:bg-brand-600 sm:min-h-12 sm:px-6`}
          >
            Continuar: {last.name}
          </Link>
        )}

        <AttendanceScopeFilter scope={scope} onScopeChange={setScope} search={search} onSearchChange={setSearch} />

        {isLoading && <LoadState label="Cargando eventos..." />}

        {isError && (
          <Alert
            variant="error"
            title="No se pudieron cargar los eventos"
            message={`${getErrorMessage(error)}. Intenta actualizar la página; el check-in por QR de un evento ya abierto sigue funcionando.`}
          />
        )}

        {data && rows.length === 0 && (
          <p className="border-y border-gray-200 px-4 py-10 text-center text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400">
            {data.length === 0 ? "Aún no hay eventos registrados." : "Ningún evento coincide con el filtro."}
          </p>
        )}

        {groups.live.length > 0 && <Group title="En curso" rows={groups.live} today={today} onOpen={(r) => navigate(scanPath(r))} />}
        {groups.upcoming.length > 0 && <Group title="Próximos" rows={groups.upcoming} today={today} onOpen={(r) => navigate(scanPath(r))} />}
        {groups.past.length > 0 && (
          <details className="group">
            <summary className="mb-2 cursor-pointer text-sm font-semibold text-gray-800 focus:outline-hidden focus-visible:ring-3 focus-visible:ring-brand-500/20 dark:text-white/90">
              Anteriores <span className="font-normal text-gray-500 dark:text-gray-400">({groups.past.length})</span>
            </summary>
            <Group title="Anteriores" showTitle={false} rows={groups.past} today={today} onOpen={(r) => navigate(scanPath(r))} />
          </details>
        )}
      </div>
    </ModuleLayout>
  )
}
